import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import Loader from "components/Loader";
import { P } from "const/permissions";
import { hasPermission } from "utils/permissionUtils";
import { CLIENT_DOCUMENT_REQUESTS_QUERY_KEY, fetchDocumentRequests } from "./clientDocuments.api";
import { filterRequests } from "./clientDocuments.utils";
import DocumentRequestDetail from "./DocumentRequestDetail";
import DocumentRequestList from "./DocumentRequestList";
import ModuleDocumentsPanel from "./ModuleDocumentsPanel";
import NewDocumentRequestDialog from "./NewDocumentRequestDialog";

const ClientDocumentsTab = ({ clientId, initialRequestId = null }) => {
  const canCreateRequest = hasPermission(P.CLIENT_DOCUMENT_REQUEST.CREATE);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Deep-linked from a document notification: land directly on the request it is about rather
  // than the bare list.
  const [selectedRequestId, setSelectedRequestId] = useState(initialRequestId);
  const [group, setGroup] = useState("collected");
  const [search, setSearch] = useState("");

  // refetchOnMount must stay "always": the QueryClient default is
  // refetchOnMount:false, so an inactive query would otherwise serve a stale
  // list when this tab is reopened after a request was created elsewhere.
  const {
    data: requests = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [CLIENT_DOCUMENT_REQUESTS_QUERY_KEY, clientId],
    queryFn: () => fetchDocumentRequests(clientId),
    enabled: Boolean(clientId),
    refetchOnMount: "always",
  });

  // Resolve from the live list rather than storing the row object: after a
  // revoke the list refetches, and a stored copy would keep rendering the old
  // status and a portalUrl that is now dead.
  const selectedRequest = requests.find((request) => request.id === selectedRequestId) || null;
  const visibleRequests = filterRequests(requests, search);

  return (
    <Box className="space-y-4">
      <Box className="flex items-center justify-between">
        <Typography variant="h6">Documents</Typography>
        {canCreateRequest && (
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Request documents
          </Button>
        )}
      </Box>

      <Tabs value={group} onChange={(event, next) => setGroup(next)}>
        <Tab value="collected" label="Collected" />
        <Tab value="modules" label="Module documents" />
      </Tabs>
      <Divider />

      {group === "modules" && <ModuleDocumentsPanel clientId={clientId} />}

      {group === "collected" && (
        <Box className="space-y-4">
          {isLoading && <Loader />}

          {!isLoading && error && (
            <Typography color="error" variant="body2">
              {error.message}
            </Typography>
          )}

          {!isLoading && !error && requests.length === 0 && (
            <Box className="border border-app-border rounded-lg p-6 text-center text-app-text-muted">
              <Typography variant="body2">
                No document requests yet. Use "Request documents" to ask this client to upload the
                files you need, they will get an emailed link to a secure upload portal.
              </Typography>
            </Box>
          )}

          {!isLoading && !error && requests.length > 0 && (
            <TextField
              size="small"
              label="Search requests"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="min-w-[220px]"
            />
          )}

          {!isLoading && !error && requests.length > 0 && visibleRequests.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Nothing matches that search.
            </Typography>
          )}

          {!isLoading && !error && visibleRequests.length > 0 && (
            <DocumentRequestList
              requests={visibleRequests}
              selectedRequestId={selectedRequestId}
              onSelect={(request) =>
                setSelectedRequestId((current) => (current === request.id ? null : request.id))
              }
            />
          )}

          {/*
            The detail opens in a modal rather than inline below the list: with many
            requests the inline panel lands far down the page and staff must scroll
            to it for every row they open.
          */}
          <Dialog
            open={Boolean(selectedRequest)}
            onClose={() => setSelectedRequestId(null)}
            maxWidth="lg"
            fullWidth
            scroll="paper"
          >
            <DialogContent dividers>
              {selectedRequest && (
                <DocumentRequestDetail
                  clientId={clientId}
                  request={selectedRequest}
                  onClose={() => setSelectedRequestId(null)}
                />
              )}
            </DialogContent>
          </Dialog>
        </Box>
      )}

      {canCreateRequest && (
        <NewDocumentRequestDialog
          clientId={clientId}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </Box>
  );
};

export default ClientDocumentsTab;
